// 设置 → Sider 200px 左侧二级导航 + Cookie 状态用 WeChat 风「已绑定/未绑定」圆点 + 状态色
// 子页:公众号 Cookie / 抓取 / 鉴权 / 数据导出 / 系统状态
// Tabs 改为 Sider + Content 布局,让设置页更接近 WeChat MP 「设置中心」结构
import { useEffect, useState } from 'react'
import {
  Form, Input, Switch, InputNumber, Button, App, Table, Tag, Alert, Spin,
  Layout as AntdLayout,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Save, Download, Database, ShieldCheck, Server, Cookie, RefreshCw, Trash2,
  QrCode, ClipboardPaste, CheckCircle2, XCircle, Loader2, Info, DatabaseBackup,
} from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import { get, post, api } from '@/lib/api'
import { timeAgo } from '@/lib/utils'
import { cn } from '@/lib/utils'

const { Sider, Content } = AntdLayout

const scrapeFormInitial = {
  scheduleTime: '01:00',
  rateLimitMs: 1000,
  enableArticleRace: true,
  enableUserAttr: true,
  enableUserSource: true,
}

const SYSTEM_INFO = {
  version: '0.1.0',
  python: '3.12.12',
  node: '20.x',
  sqlite: '3.45+',
  diskUsage: '— / 50 GB',
  uptime: '—',
}

interface CookieStatus {
  bound: boolean
  valid: boolean
  publicName: string | null
  bizId: string | null
  verifiedAt: number
  expiresAt: number
  expiresInHours: number
  lastProbeStatus: 'success' | 'expired' | 'failed' | 'no_cookie' | 'never_probed' | null
  // 2026-06-04:backfill 状态
  backfillStatus: 'running' | 'success' | 'partial' | 'failed' | 'never_run'
  backfillLastRunAt: number
  backfillTotalRows: number
}

interface BackfillResp {
  started: boolean
  message: string
}

interface CookieVerifyResp extends CookieStatus {
  message: string
}

interface LoginBeginResp {
  sessionId: string
  state: string
  message: string
}

type LoginState = 'starting' | 'waiting' | 'scanned' | 'confirmed' | 'expired' | 'failed'

interface LoginStatusResp {
  sessionId: string
  state: LoginState
  qrImage: string | null
  errorMsg: string | null
  expiresAt: number
  expiresInSeconds: number
  publicName: string | null
  bizId: string | null
}

function StateChip({ state }: { state: LoginState }) {
  const map: Record<LoginState, { color: string; label: string; icon: JSX.Element }> = {
    starting: { color: 'processing', label: '正在启动浏览器…', icon: <Loader2 size={12} className="animate-spin" /> },
    waiting:  { color: 'processing', label: '等待你扫码',       icon: <QrCode size={12} /> },
    scanned:  { color: 'warning', label: '已扫码,请在手机上确认', icon: <Spin size="small" /> },
    confirmed:{ color: 'success',    label: '已登录',            icon: <CheckCircle2 size={12} /> },
    expired:  { color: 'warning',    label: '已超时',            icon: <XCircle size={12} /> },
    failed:   { color: 'error',      label: '失败',              icon: <XCircle size={12} /> },
  }
  const m = map[state] || { color: 'default', label: state, icon: null }
  return <Tag color={m.color} className="!inline-flex !items-center !gap-1">{m.icon}{m.label}</Tag>
}

function CookieTab() {
  const { message, modal } = App.useApp()
  const qc = useQueryClient()
  const [pasteRaw, setPasteRaw] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [loginErr, setLoginErr] = useState<string | null>(null)

  const statusQ = useQuery({
    queryKey: ['cookies', 'status'],
    queryFn: () => get<CookieStatus>('/auth/cookies/status'),
    refetchInterval: 30_000,
  })

  const beginMu = useMutation({
    mutationFn: () => post<LoginBeginResp>('/auth/cookies/begin'),
    onSuccess: (data) => {
      setSessionId(data.sessionId)
      setLoginErr(null)
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { error?: { code?: string; message?: string } } } })
        ?.response?.data?.error
      setLoginErr(detail?.message || '启动扫码登录失败')
    },
  })

  const loginStatusQ = useQuery<LoginStatusResp>({
    queryKey: ['cookies', 'login', sessionId],
    queryFn: () => get<LoginStatusResp>(`/auth/cookies/begin/${sessionId}`),
    enabled: !!sessionId,
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data) return 2_000
      if (data.state === 'confirmed' || data.state === 'expired' || data.state === 'failed') return false
      return 2_000
    },
  })

  useEffect(() => {
    const s = loginStatusQ.data
    if (s?.state === 'confirmed') {
      message.success(`已绑定公众号「${s.publicName || '已识别'}」`)
      qc.invalidateQueries({ queryKey: ['cookies', 'status'] })
      const t = setTimeout(() => setSessionId(null), 3000)
      return () => clearTimeout(t)
    }
    if (s?.state === 'failed' || s?.state === 'expired') {
      setLoginErr(s.errorMsg || (s.state === 'expired' ? '扫码超时,请重试' : '登录失败'))
    }
  }, [loginStatusQ.data, message, qc])

  const pasteMu = useMutation({
    mutationFn: (input: string) => post<CookieVerifyResp>('/auth/cookies', { raw: input }),
    onSuccess: (data) => {
      message.success(data.message || 'Cookie 已加密保存')
      setPasteRaw('')
      qc.invalidateQueries({ queryKey: ['cookies', 'status'] })
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      message.error(detail || '校验失败 — 请确认 Cookie 完整')
    },
  })

  const deleteMu = useMutation({
    mutationFn: async () => { await api.delete('/auth/cookies') },
    onSuccess: () => {
      message.success('已清除 Cookie')
      qc.invalidateQueries({ queryKey: ['cookies', 'status'] })
    },
  })

  // 2026-06-04:手动重跑历史数据回填
  const backfillMu = useMutation({
    mutationFn: () => post<BackfillResp>('/auth/cookies/backfill'),
    onSuccess: (data) => {
      if (data.started) {
        message.success(data.message)
        qc.invalidateQueries({ queryKey: ['cookies', 'status'] })
      } else {
        message.warning(data.message)
      }
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      message.error(detail?.message || '回填触发失败')
    },
  })

  const status = statusQ.data
  const bound = status?.bound
  const valid = status?.valid

  function onClearClick() {
    modal.confirm({
      title: '清除公众号 Cookie？',
      content: '清除后抓取层将无法工作,需要重新扫码或粘贴一次。',
      okType: 'danger',
      okText: '确认清除',
      cancelText: '取消',
      onOk: () => deleteMu.mutate(),
    })
  }

  function statusBadge() {
    if (statusQ.isLoading) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-ink-500">
          <Spin size="small" /> 加载中
        </span>
      )
    }
    if (!bound) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-ink-500">
          <span className="status-dot idle" /> 未绑定
        </span>
      )
    }
    if (valid) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-success">
          <span className="status-dot success" />
          已绑定 · {status?.publicName || '已识别'}
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-danger">
        <span className="status-dot danger" /> 已失效
      </span>
    )
  }

  const loginActive = sessionId && loginStatusQ.data &&
    !['confirmed', 'expired', 'failed'].includes(loginStatusQ.data.state)

  return (
    <div className="pt-1 space-y-5 max-w-3xl">
      {/* 状态卡 — WeChat 风:圆点 + 状态色 */}
      <div className="border border-ink-200 rounded p-4 bg-white">
        <div className="flex items-start gap-3">
          <Cookie size={20} className="text-primary-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-ink-900">公众号后台 Cookie 状态</h3>
              {statusBadge()}
            </div>
            {bound && (
              <div className="mt-2 text-xs text-ink-500 space-y-0.5">
                <div>公众号 ID：<span className="num text-ink-900">{status?.bizId || '—'}</span></div>
                <div>上次验证：<span className="num">{status?.verifiedAt ? timeAgo(status.verifiedAt * 1000) : '—'}</span></div>
                <div>
                  状态:
                  <span className={`num ml-1 ${valid ? 'text-ink-900' : 'text-danger'}`}>
                    {valid ? (
                      status?.lastProbeStatus === 'never_probed'
                        ? '永久保留(待首次探活)'
                        : '永久保留 · 探活正常'
                    ) : (
                      'Cookie 已失效,需重新扫码或粘贴'
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="small" icon={<RefreshCw size={12} />} onClick={() => statusQ.refetch()} loading={statusQ.isFetching}>刷新</Button>
            {bound && status?.backfillStatus !== 'running' && (
              <Button
                size="small"
                icon={<DatabaseBackup size={12} />}
                onClick={() => backfillMu.mutate()}
                loading={backfillMu.isPending}
                title="重跑回填:7 天 metric + 7 天 user + 全量文章"
              >
                重跑回填
              </Button>
            )}
            {bound && <Button size="small" danger icon={<Trash2 size={12} />} onClick={onClearClick}>清除</Button>}
          </div>
        </div>
      </div>

      {/* 扫码登录 */}
      <div className="border border-primary-200 rounded p-5 bg-primary-50/30">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-medium text-ink-900 flex items-center gap-1.5">
              <QrCode size={16} className="text-primary-600" /> 扫码登录(推荐)
            </h3>
            <p className="text-xs text-ink-500 mt-1">
              fenxi 服务端开浏览器代理 mp.weixin.qq.com 登录页 → 截二维码给你扫 → 拿到 Cookie 后立即加密入库,浏览器自动关闭
            </p>
          </div>
          {!loginActive && (
            <Button
              type="primary"
              icon={<QrCode size={14} />}
              loading={beginMu.isPending}
              onClick={() => beginMu.mutate()}
            >
              {bound ? '重新扫码' : '开始扫码'}
            </Button>
          )}
        </div>

        {loginErr && <Alert type="error" message={loginErr} closable showIcon className="!mb-3" onClose={() => setLoginErr(null)} />}

        {sessionId && loginStatusQ.data && (
          <div className="bg-white rounded border border-ink-200 p-4 flex items-start gap-4">
            <div className="w-48 h-48 bg-ink-50 rounded flex items-center justify-center overflow-hidden shrink-0">
              {loginStatusQ.data.qrImage ? (
                <img
                  src={loginStatusQ.data.qrImage}
                  alt="扫码登录二维码"
                  className="w-full h-full object-contain"
                />
              ) : (
                <Spin tip="正在加载二维码…" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-2"><StateChip state={loginStatusQ.data.state} /></div>
              <div className="text-sm text-ink-700 space-y-1">
                <div>1. 打开 <strong>微信 → 我 → 设置 → 通用 → 扫一扫</strong>(或顶部加号 → 扫一扫)</div>
                <div>2. 扫描左侧二维码</div>
                <div>3. 在手机上点击「确认登录」</div>
              </div>
              <div className="mt-3 text-xs text-ink-500">
                剩余时间:<span className="num">{loginStatusQ.data.expiresInSeconds}s</span>
                {loginStatusQ.data.state === 'confirmed' && loginStatusQ.data.publicName && (
                  <span className="ml-3 text-success">已绑定「{loginStatusQ.data.publicName}」</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fallback: 手动粘贴 */}
      <div className="border border-ink-200 rounded p-4">
        <div className="flex items-center gap-1.5 mb-3 text-sm text-ink-700">
          <ClipboardPaste size={14} /> 备用:手动粘贴 Cookie(扫码不可用时用)
        </div>
        <Alert
          type="info"
          showIcon
          icon={<Info size={16} />}
          className="!mb-3"
          message="如何获取 Cookie"
          description={
            <ol className="list-decimal pl-5 space-y-1 text-sm leading-relaxed">
              <li>新开标签页打开 <a href="https://mp.weixin.qq.com" target="_blank" rel="noreferrer">mp.weixin.qq.com</a>,用微信扫码登录</li>
              <li>登录成功后按 <kbd>F12</kbd> 打开开发者工具,切到 <strong>Network</strong> 面板</li>
              <li>刷新页面,找一条 mp.weixin.qq.com 请求 → 右侧 Headers → Request Headers → 找 <code>Cookie:</code> 那一行</li>
              <li>右键 → Copy value → 粘贴到下面 → 点检测</li>
            </ol>
          }
        />
        <Form layout="vertical">
          <Form.Item label="Cookie 内容">
            <Input.TextArea
              rows={5}
              value={pasteRaw}
              onChange={(e) => setPasteRaw(e.target.value)}
              placeholder="slave_user=gh_xxx; slave_sid=xxx; data_bizuin=xxx; ..."
              spellCheck={false}
              autoComplete="off"
              className="!text-xs"
            />
          </Form.Item>
          <Button
            icon={<ShieldCheck size={14} />}
            loading={pasteMu.isPending}
            onClick={() => pasteRaw.trim() ? pasteMu.mutate(pasteRaw) : message.warning('请先粘贴 Cookie 内容')}
          >
            检测并保存
          </Button>
        </Form>
      </div>
    </div>
  )
}

function ScrapeTab() {
  const { message } = App.useApp()
  const [scrapeForm] = Form.useForm()

  const handleSaveScrape = () => {
    scrapeForm.validateFields().then(() => {
      message.success('抓取配置已保存(M7 上线后真实生效)')
    })
  }

  return (
    <Form form={scrapeForm} layout="vertical" initialValues={scrapeFormInitial} className="max-w-xl pt-1">
      <Form.Item label="调度时间" name="scheduleTime"><Input placeholder="HH:MM" /></Form.Item>
      <Form.Item label="请求间隔 (ms)" name="rateLimitMs"><InputNumber min={1000} max={10000} step={100} className="!w-full" /></Form.Item>
      <Form.Item label="启用 7 天抢抓" name="enableArticleRace" valuePropName="checked"><Switch /></Form.Item>
      <Form.Item label="抓取用户属性" name="enableUserAttr" valuePropName="checked"><Switch /></Form.Item>
      <Form.Item label="抓取增长来源" name="enableUserSource" valuePropName="checked"><Switch /></Form.Item>
      <Button type="primary" icon={<Save size={14} />} onClick={handleSaveScrape}>保存配置</Button>
    </Form>
  )
}

function AuthTab() {
  return (
    <div className="pt-1 max-w-2xl">
      <h3 className="text-base font-medium text-ink-900 mb-3">修改密码</h3>
      <p className="text-xs text-ink-500 mb-3">
        M4 占位 — 改密目前需要直接编辑 backend/.env: AUTH_PASS 后 systemd restart fenxi
      </p>
      <Form layout="vertical">
        <Form.Item label="当前密码"><Input.Password disabled /></Form.Item>
        <Form.Item label="新密码"><Input.Password disabled /></Form.Item>
        <Form.Item label="确认新密码"><Input.Password disabled /></Form.Item>
        <Button type="primary" disabled>更新密码(待实现)</Button>
      </Form>
    </div>
  )
}

function ExportTab() {
  return (
    <div className="pt-1 space-y-4 max-w-xl">
      <div className="border border-ink-200 rounded p-4">
        <div className="flex items-start gap-3">
          <Database size={20} className="text-primary-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-ink-900">CSV 导出</h3>
            <p className="text-xs text-ink-500 mt-1 mb-3">GET /api/export/csv?table=…</p>
            <Button size="small" icon={<Download size={12} />} onClick={() => window.open('/api/export/csv?table=articles', '_blank')}>导出 articles</Button>
          </div>
        </div>
      </div>
      <div className="border border-ink-200 rounded p-4">
        <div className="flex items-start gap-3">
          <Database size={20} className="text-primary-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-ink-900">SQLite 文件下载</h3>
            <p className="text-xs text-ink-500 mt-1 mb-3">完整 fenxi.db 文件</p>
            <Button size="small" icon={<Download size={12} />} onClick={() => window.open('/api/export/db', '_blank')}>下载 .db</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function SystemTab() {
  return (
    <div className="pt-1 max-w-xl">
      <Table
        size="small"
        showHeader={false}
        pagination={false}
        dataSource={Object.entries(SYSTEM_INFO)}
        rowKey={(r) => r[0]}
        columns={[
          { title: 'key', dataIndex: 0, width: 180, render: (v: string) => <span className="text-ink-500">{v}</span> },
          { title: 'value', dataIndex: 1, render: (v: string) => <span className="num text-ink-900 font-medium">{v}</span> },
        ]}
      />
    </div>
  )
}

type SettingKey = 'cookie' | 'scrape' | 'auth' | 'export' | 'system'

const SETTING_MENU: Array<{ key: SettingKey; label: string; icon: JSX.Element }> = [
  { key: 'cookie', label: '公众号 Cookie', icon: <Cookie size={14} /> },
  { key: 'scrape', label: '抓取配置', icon: <Server size={14} /> },
  { key: 'auth', label: '鉴权', icon: <ShieldCheck size={14} /> },
  { key: 'export', label: '数据导出', icon: <Download size={14} /> },
  { key: 'system', label: '系统状态', icon: <Server size={14} /> },
]

export default function Settings() {
  const [active, setActive] = useState<SettingKey>('cookie')

  return (
    <div>
      <PageHeader title="设置" subtitle="公众号 Cookie、抓取配置、鉴权、数据导出与系统状态" />

      <div className="bg-white border border-ink-200 rounded">
        <AntdLayout className="!bg-white">
          {/* 左侧二级导航 200px */}
          <Sider width={200} className="!bg-ink-50 !border-r !border-ink-200 !py-2">
            <nav className="flex flex-col">
              {SETTING_MENU.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setActive(m.key)}
                  className={cn(
                    'flex items-center gap-2 px-4 h-9 text-[13px] text-left transition-colors',
                    active === m.key
                      ? 'bg-white text-primary-600 font-medium border-l-2 border-primary-500'
                      : 'text-ink-700 hover:bg-white/60 border-l-2 border-transparent',
                  )}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </nav>
          </Sider>
          <Content className="!p-5">
            {active === 'cookie' && <CookieTab />}
            {active === 'scrape' && <ScrapeTab />}
            {active === 'auth' && <AuthTab />}
            {active === 'export' && <ExportTab />}
            {active === 'system' && <SystemTab />}
          </Content>
        </AntdLayout>
      </div>
    </div>
  )
}
